/**
 * Share-target landing (Android "Compartir → Gastos"). sw.js parks the shared files in a cache and redirects here;
 * this screen lists them, asks which report, then POSTs to /trips/{tripId}/receipts like every other intake.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, call, filesForm } from '../api/client.ts';
import { useLoad } from '../state/hooks.ts';
import { tripMeta } from './Viajes.tsx';
import { Button, ErrorNote, Icon, Loading, TopBar } from '../theme/ui.tsx';
import { SplitSheet, isSingleTicket, needsQuestion, useSharedBills } from './split.tsx';

const SHARED = 'viaticos-shared-files';
async function readShared(): Promise<File[]> {
  if (!('caches' in window)) return [];
  const cache = await caches.open(SHARED); const out: File[] = [];
  for (const req of await cache.keys()) {
    const res = await cache.match(req); if (!res) continue;
    const name = decodeURIComponent(res.headers.get('X-File-Name') ?? 'archivo'); const type = res.headers.get('Content-Type') ?? 'application/octet-stream';
    out.push(new File([await res.blob()], name, { type }));
  }
  return out;
}
async function clearShared() { if ('caches' in window) await caches.delete(SHARED); }

export default function Compartir() {
  const [sp] = useSearchParams(); const nav = useNavigate();
  const [files, setFiles] = useState<File[] | null>(null);
  const trips = useLoad(() => call('trips.list', { query: { archived: 'false' } }), []);
  const [tripId, setTripId] = useState<string | null>(sp.get('reporte'));
  const [busy, setBusy] = useState(false); const [error, setError] = useState<string | null>(null);
  useEffect(() => { readShared().then(setFiles); }, [sp]);
  const heic = (files ?? []).filter((f) => /heic|heif/i.test(f.type) || /\.(heic|heif)$/i.test(f.name));
  // shared bills (flag on): one ticket shared from Gmail or Files gets the question first; a batch goes up unanswered and Por revisar asks per receipt
  const sharedBills = useSharedBills(); const [asking, setAsking] = useState(false);
  const ask = !!sharedBills && !!files && isSingleTicket(files) && needsQuestion(files);

  const send = async (body?: FormData) => {
    if (!tripId || !files?.length) return; setBusy(true); setError(null);
    try {
      const res = await call('receipts.upload', { params: { tripId }, body: body ?? filesForm(files) });
      await clearShared();
      nav(`/reporte/${tripId}`, { replace: true, state: { toast: `${res.receiptIds.length === 1 ? '1 comprobante agregado' : `${res.receiptIds.length} comprobantes agregados`}${res.ignored.length ? ` · ${res.ignored.length} ignorado(s)` : ''}. Los estamos leyendo.` } });
    } catch (e) { setError(e instanceof ApiError ? e.message : 'No se pudo subir.'); setBusy(false); }
  };

  if (files === null || trips.loading) return <div className="screen"><Loading /></div>;
  if (trips.error) return <div className="screen"><ErrorNote error={trips.error} retry={trips.reload} /></div>;
  const list = trips.data?.trips ?? [];
  return (
    <div className="screen">
      <div className="phone">
        <TopBar close="/" title="Compartir a Gastos" muted={false} />
        <div className="pad" style={{ paddingTop: 12, paddingBottom: 18 }}>
          {files.length === 0
            ? <div className="lead">No llegó ningún archivo. Desde Gmail o Archivos, elige <b>Compartir → Gastos</b> con el PDF, XML, ZIP o la foto del comprobante.</div>
            : <div className="lead">{files.length === 1 ? 'Un archivo' : `${files.length} archivos`} para agregar. Elige el reporte al que pertenecen.</div>}
        </div>
        {files.length > 0 && (
          <div className="pad" style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingBottom: 20 }}>
            {files.map((f, i) => <div key={i} className="kv"><span className="row" style={{ gap: 10, minWidth: 0 }}><Icon.File size={17} color="var(--ink-2)" width={1.8} /><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span></span><span className="kv__v mono sub" style={{ fontSize: 12 }}>{(f.size / 1024).toFixed(0)} KB</span></div>)}
            {heic.length > 0 && <div style={{ color: 'var(--red)', fontSize: 13 }}>Los archivos HEIC no se aceptan; comparte la foto como JPEG.</div>}
          </div>
        )}
        {files.length > 0 && (
          <>
            <div className="label" style={{ padding: '0 20px 10px' }}>Reporte</div>
            <div className="pad" style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              {list.map((t) => (
                <button key={t.tripId} className={`card card--pad ${tripId === t.tripId ? '' : ''}`} style={{ textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12, border: tripId === t.tripId ? '1.5px solid var(--ink)' : undefined }} onClick={() => setTripId(t.tripId)}>
                  <div className="grow"><div className="h4" style={{ fontSize: 15 }}>{t.name}</div><div className="sub sub--xs" style={{ marginTop: 3 }}>{tripMeta(t)}</div></div>
                  {tripId === t.tripId && <Icon.Check size={18} width={2.2} />}
                </button>
              ))}
              <Link to="/nuevo?then=/compartir" className="btn btn--ghost btn--inline" style={{ justifyContent: 'flex-start', gap: 8 }}><Icon.Plus size={17} color="var(--ink-2)" />Nuevo reporte</Link>
            </div>
          </>
        )}
        {error && <div className="pad" style={{ color: 'var(--red)', fontSize: 13, paddingTop: 12 }}>{error}</div>}
        <div className="footer">
          {files.length > 0 ? <Button onClick={() => (ask ? setAsking(true) : void send())} busy={busy} disabled={!tripId || heic.length > 0 || sharedBills === undefined} icon={<Icon.Upload size={18} width={1.9} />}>Agregar al reporte</Button> : <Button to="/">Ir a Gastos</Button>}
        </div>
        {asking && <SplitSheet files={files} onCancel={() => setAsking(false)} onSend={async (fd) => { setAsking(false); await send(fd); }} />}
      </div>
    </div>
  );
}
